/**
 * PlanStatusBadge - Status indicator for plan states
 *
 * Displays plan status with semantic color coding:
 * - active: emerald (available for new subscribers)
 * - inactive: slate (hidden from new subscribers)
 * - deprecated: amber (legacy, being phased out)
 */
import { cn } from '@respectly/ui';
import type { PlanStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface PlanStatusBadgeProps {
  status: PlanStatus;
  showDot?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// Configuration
// ============================================

const statusConfig: Record<
  PlanStatus,
  { label: string; dotColor: string; badgeClass: string }
> = {
  active: {
    label: 'Active',
    dotColor: 'bg-emerald-500',
    badgeClass:
      'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
  },
  inactive: {
    label: 'Inactive',
    dotColor: 'bg-slate-500',
    badgeClass:
      'bg-slate-500/10 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 border-slate-500/20',
  },
  deprecated: {
    label: 'Deprecated',
    dotColor: 'bg-amber-500',
    badgeClass:
      'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const dotSizeClasses = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

// ============================================
// Component
// ============================================

export function PlanStatusBadge({
  status,
  showDot = true,
  size = 'md',
  className,
}: PlanStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        sizeClasses[size],
        config.badgeClass,
        className
      )}
    >
      {showDot && (
        <span
          className={cn(
            'rounded-full',
            dotSizeClasses[size],
            config.dotColor
          )}
        />
      )}
      {config.label}
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

export function getPlanStatusColor(status: PlanStatus): string {
  const colors: Record<PlanStatus, string> = {
    active: '#10b981',
    inactive: '#64748b',
    deprecated: '#f59e0b',
  };
  return colors[status];
}

export function getPlanStatusLabel(status: PlanStatus): string {
  return statusConfig[status].label;
}
